import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { GUIDE_CARD_TITLE_CLAMP_CSS } from './guideCardWidth';
import { GUIDE_BROWSE_TILE_COUNT } from './publishedGuides';

/**
 * The one guide card: an emotive title over a 3-tile peek strip of product
 * photos from the guide itself. Extracted from GuideBrowse (where this markup
 * was module-private) so the /gift-guides browse grid and the homepage
 * "Trending gift guides" rail compose the SAME card instead of forking it —
 * a reorder or restyle lands on both surfaces at once.
 *
 * Purely presentational: callers resolve published listings and the
 * strip's image urls, and own the layout box around the
 * card. This component owns only what a card IS, never where it sits.
 */

const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(${GUIDE_BROWSE_TILE_COUNT}, 1fr);
  gap: 2px;
  border-radius: ${({ theme }) => theme.radius['2xl']};
  overflow: hidden;
`;

const Section = styled(Link)<{
  $alignStripToBottom?: boolean;
  $titleBelow?: boolean;
}>`
  display: block;
  padding: 14px 0 18px;
  text-decoration: none;
  color: inherit;
  -webkit-tap-highlight-color: transparent;
  /* PRESS FEEDBACK, and it is owed by the line directly above. Suppressing the
     tap highlight removes the only press affordance iOS Safari gives a link,
     and nothing else here replaces it: the one other interactive style in this
     file is a hover on the title colour, and a phone has no hover. So a tap
     used to produce no response at all until the next page painted — long
     enough on a slow connection to read as a dropped tap and earn a second one.
     Duration is --dur-press; the chip in ResultsProductCard is the pattern.

     Only the PRESSED state carries a transform, and the resting card
     deliberately has no scale(1). A resting identity transform is a standing
     smell in this codebase — it keeps an element permanently promoted to its
     own compositing layer, which is why the sticky bars were walked back to
     transform: none at rest (THE-520) — and it is free to avoid here, because
     none interpolates as the identity transform, so the press still animates
     both ways without leaving a layer behind between taps.

     scale, not a background shift, because the standard splits those by control
     type: cards scale, chips shift. */
  transition: transform 150ms ease;
  &:active {
    transform: scale(0.98);
  }
  /* A transform-based press state is motion, so it goes when motion does — the
     press still registers, it just no longer moves. The transition dies with it
     or the suppressed state would animate its way to standing still. */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:active {
      transform: none;
    }
  }
  /* Single-row rail mode. The rail's Track is a flex row, so every card is
     already stretched to the tallest card's height; making the card itself a
     column and letting the strip's auto top-margin eat the slack pins all the
     strips to a shared bottom edge. Equal card widths ⇒ equal strip heights ⇒
     one shared strip baseline, at ANY viewport width, title length, or line
     count. The grid on /gift-guides wants titles to hug their strips, so this
     is opt-in and the default card is unchanged.

     THE STRIP PUSH IS DROPPED WHEN THE TITLE IS BELOW, and that is not a
     simplification — it is what makes the two options compose. With the title
     under it the strip is the FIRST child, so an auto top margin would shove
     the strip and the title it carries down to the bottom of a stretched card
     and open a ragged gap above every short-titled one. It is also redundant
     there: the shared baseline it buys is free in this order, because equal
     card widths give equal strip heights and the strips all start at the card's
     top edge. The slack lands under the titles instead, where nothing lines up
     against it. */
  ${({ $alignStripToBottom, $titleBelow }) =>
    $alignStripToBottom
      ? `
        display: flex;
        flex-direction: column;
        ${
          $titleBelow
            ? ''
            : `
        ${Strip} {
          margin-top: auto;
        }
        `
        }
      `
      : ''}
`;

/* The width at which the card's TYPE steps up. Exported because a caller that
   sizes the card's column (the homepage rail) has to step its width on the
   same breakpoint: a card carrying 22px type at the 150px mobile width wraps
   its titles a line further than it does on either side of the band, which is
   how THE-183's 768–1023px misalignment happened. Import this rather than
   re-typing 768 — the two bumps only stay coupled if they read one number. */
export const GUIDE_CARD_TYPE_BREAKPOINT_PX = 768;

// Sized to the Pinterest browse-feed register: the title reads as a section
// label over the strip, not a page-level display headline.
const Title = styled.h3<{ $below?: boolean }>`
  /* The inner span keeps caption padding outside the two-line clamp. */
  [data-guide-title] {
    ${GUIDE_CARD_TITLE_CLAMP_CSS}
    min-width: 0;
    min-height: 2.4em;
    max-height: 2.4em;
  }
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.015em;
  line-height: 1.2;
  /* The gap follows the title to whichever side of the strip it is on. Slightly
     wider below (12 vs 10) because a caption hanging under a photo needs a
     touch more air to stop reading as part of the image than a label sitting
     above one does. */
  margin: ${({ $below }) => ($below ? '12px 0 0' : '0 0 10px')};
  color: hsl(var(--foreground));
  text-wrap: balance;
  transition: color var(--dur-press) ease;
  /* THE-876 rule 4: every card reserves exactly 2 title lines so a standard-
     width row's cards line up regardless of title length — em-based so the
     reserve tracks the font-size step below rather than needing its own. */
  /* Clamp the inner span so caption padding cannot reveal a third line. */
  ${Section}:hover & {
    color: ${({ theme }) => theme.color.clay};
  }
  @media (min-width: ${GUIDE_CARD_TYPE_BREAKPOINT_PX}px) {
    font-size: 22px;
  }
`;

// The emoji and the title's last word travel together — see PinnedTail.
const TitleEmoji = styled.span`
  font-size: 0.82em;
`;

/* Keeps the trailing emoji glued to the last word of the title. Without this
   an emoji after a space is its own break opportunity, so a wrapping title
   strands it alone on the final line ("Spooky Season Style" + a lone 🦇).
   nowrap + a non-breaking space makes "word␣emoji" one unbreakable unit; the
   title still wraps freely at every earlier space. */
const PinnedTail = styled.span`
  white-space: nowrap;
`;

const Tile = styled.div`
  aspect-ratio: 2 / 3;
  background: ${({ theme }) => theme.color.cream};
  overflow: hidden;
`;

const TileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const NBSP = ' ';

const Caption = styled.p`
  margin: 6px 0 0;
  color: #6b6b6b;
  font-size: 14px;
  line-height: 1.35;
  overflow-wrap: anywhere;
  span {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }
`;

export interface GuidePeekCardProps {
  href: string;
  title: string;
  emoji: string;
  /** Peek image urls — only the first GUIDE_BROWSE_TILE_COUNT render here;
   *  extras are ignored. Short/empty → the strip pads with placeholder tiles. */
  imageUrls: string[];
  /**
   * Passed the native click event, not just notified — THE-950's board rail
   * intercepts a plain click (to open the guide as a sheet/pop-up over the
   * board instead of navigating) while letting a modified click (⌘/Ctrl/
   * middle-click, which the browser already routes to a new tab via `to`
   * below) through untouched. Every other caller just ignores the argument,
   * so this is additive, not a breaking change.
   */
  onClick?: (event: React.MouseEvent) => void;
  /** Pin the peek strip to the bottom of the card so sibling cards in a
   *  stretched single row share one strip baseline regardless of how many
   *  lines each title wraps to. Rails pass it; the browse grid omits it.
   *  Requires the caller to stretch its cards to equal height (a flex row
   *  does this by default) — otherwise there is no slack to absorb. */
  alignStripToBottom?: boolean;
  /**
   * Put the title UNDER the photo strip instead of over it (THE-729 item #3).
   *
   * Opt-in, and taken only by the recipient board's rail: the board asked for
   * this change *"only ... on the recipient boards — all other gift guide pages
   * stay the same"*, so /gift-guides, the hub grid and the marketing homepage
   * rail all keep the title on top by leaving this unset.
   *
   * Reorders the DOM, not just the paint, so the reading order a screen reader
   * and the tab-into-card order both follow what is on screen. (The whole card
   * is one link with an `aria-label` of the title, so this changes nothing
   * about how it is announced — only about what a sighted user reads first.)
   */
  titleBelow?: boolean;
  /** react-router link `state` carried to the guide page. The board rail uses
   *  it to hand the guide its origin ({ from: { recipientId, boardPath,
   *  recipientName } }) so the guide can render a "← <Recipient>'s board"
   *  link. `unknown` rather than a typed origin on purpose: this card is
   *  purely presentational and must not learn about boards — the shape is the
   *  caller's contract with the destination (see useGuideBackLink). */
  linkState?: unknown;
  /** Optional non-interactive context inside the card's existing guide link. */
  caption?: string;
  /** Lets a caller's styled() wrapper target the card's root (rail sizing). */
  className?: string;
}

export const GuidePeekCard: React.FC<GuidePeekCardProps> = ({
  href,
  title,
  emoji,
  imageUrls,
  onClick,
  alignStripToBottom,
  titleBelow,
  linkState,
  caption,
  className,
}) => {
  // Split the title so only its FINAL word is bound to the emoji; everything
  // before it wraps normally.
  const lastSpace = title.lastIndexOf(' ');
  const head = lastSpace === -1 ? '' : title.slice(0, lastSpace + 1);
  const lastWord = lastSpace === -1 ? title : title.slice(lastSpace + 1);

  /* Built once and PLACED, rather than written out twice in two orders: the
     emoji-pinning, the testid and the tile loop are all things a future change
     would otherwise have to remember to make in both branches. */
  const titleEl = (
    <Title $below={titleBelow}><span data-guide-title>
      {head}
      {/* testid deliberately avoids the `guide-peek-` prefix: the strip's
          tiles use it, and callers count them with a getAllByTestId(/guide-peek/)
          regex that this span would otherwise inflate. */}
      <PinnedTail data-testid="guide-title-tail">
        {lastWord}
        {NBSP}
        <TitleEmoji aria-hidden="true">{emoji}</TitleEmoji>
      </PinnedTail>
    </span></Title>
  );

  const stripEl = (
    <Strip>
      {Array.from({ length: GUIDE_BROWSE_TILE_COUNT }, (_, i) => {
        const url = imageUrls[i];
        return (
          <Tile
            key={i}
            data-testid={url ? 'guide-peek-tile' : 'guide-peek-placeholder'}
          >
            {url ? <TileImage src={url} alt="" loading="lazy" /> : null}
          </Tile>
        );
      })}
    </Strip>
  );

  return (
    <Section
      to={href}
      state={linkState}
      onClick={onClick}
      aria-label={caption ? `${title}, ${caption}` : title}
      className={className}
      $alignStripToBottom={alignStripToBottom}
      $titleBelow={titleBelow}
      data-title-below={titleBelow ? 'true' : undefined}
    >
      {titleBelow ? (
        <>
          {stripEl}
          {titleEl}
        </>
      ) : (
        <>
          {titleEl}
          {stripEl}
        </>
      )}
      {caption && <Caption data-guide-caption title={caption}><span>{caption}</span></Caption>}
    </Section>
  );
};

export default GuidePeekCard;
